
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { 
  Bookmark, BookmarkPlus, Folder, FolderPlus, 
  Search, Plus, MoreHorizontal, Edit, Trash
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { 
  getSavedPrompts, 
  getPromptCollections, 
  createCollection,
  updateCollection,
  deleteCollection,
  getCollectionPrompts,
  SavedPrompt,
  PromptCollection 
} from "@/services/savedPromptService";
import PromptCard from "@/components/prompts/PromptCard";
import PromptGrid from "@/components/prompts/PromptGrid";

const EmptyState = ({ message, action }: { message: string; action?: React.ReactNode }) => (
  <div className="flex flex-col items-center justify-center py-20">
    <Bookmark className="h-16 w-16 text-muted-foreground/30 mb-4" />
    <p className="text-muted-foreground text-lg mb-4">{message}</p>
    {action}
  </div>
);

const LoadingState = () => (
  <div className="flex justify-center items-center py-20">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
  </div>
);

const PromptLibraryPage = () => {
  const [activeTab, setActiveTab] = useState("saved");
  const [savedPrompts, setSavedPrompts] = useState<SavedPrompt[]>([]);
  const [collections, setCollections] = useState<PromptCollection[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<PromptCollection | null>(null);
  const [collectionPrompts, setCollectionPrompts] = useState<SavedPrompt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showNewCollectionDialog, setShowNewCollectionDialog] = useState(false);
  const [showEditCollectionDialog, setShowEditCollectionDialog] = useState(false);
  const [collectionName, setCollectionName] = useState("");
  const [collectionDescription, setCollectionDescription] = useState("");
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate("/sign-in");
      return;
    }

    const loadLibraryData = async () => {
      setIsLoading(true);
      
      if (activeTab === "saved") {
        const prompts = await getSavedPrompts(false);
        setSavedPrompts(prompts);
      } else if (activeTab === "collections") {
        const collectionsData = await getPromptCollections();
        setCollections(collectionsData);
        
        if (selectedCollection) {
          const stillExists = collectionsData.find(c => c.id === selectedCollection.id);
          if (stillExists) {
            setSelectedCollection(stillExists);
            const collectionPrompts = await getCollectionPrompts(selectedCollection.id);
            setCollectionPrompts(collectionPrompts);
          } else {
            setSelectedCollection(null);
            setCollectionPrompts([]);
          }
        }
      }
      
      setIsLoading(false);
    };
    
    loadLibraryData();
  }, [user, navigate, activeTab, selectedCollection?.id]);

  const filterPrompts = (prompts: SavedPrompt[]) => {
    if (!searchTerm.trim()) return prompts;
    
    const term = searchTerm.toLowerCase();
    return prompts.filter(prompt => 
      (prompt.custom_title?.toLowerCase() || prompt.prompt.title.toLowerCase()).includes(term) ||
      prompt.prompt.description.toLowerCase().includes(term) ||
      prompt.prompt.content.toLowerCase().includes(term)
    );
  };

  const mapSavedPromptToCardProps = (savedPrompt: SavedPrompt) => {
    return {
      id: savedPrompt.prompt.id,
      title: savedPrompt.custom_title || savedPrompt.prompt.title,
      description: savedPrompt.prompt.description,
      content: savedPrompt.prompt.content,
      category: savedPrompt.prompt.category.name,
      category_id: savedPrompt.prompt.id, // Using prompt.id as the category_id since the real category_id isn't available
      likes: 0,
      reviews: 0,
      copies: 0,
      author: { 
        name: savedPrompt.prompt.author_name || "Unknown", 
        id: savedPrompt.prompt.user_id || "" 
      },
      createdAt: savedPrompt.saved_at
    };
  };

  const handleCreateCollection = async () => {
    if (!collectionName.trim()) return;
    
    const newCollection = await createCollection(collectionName, collectionDescription);
    if (newCollection) {
      setCollections(prev => [...prev, newCollection]);
      setShowNewCollectionDialog(false);
      setCollectionName("");
      setCollectionDescription("");
    }
  };

  const handleEditCollection = async () => {
    if (!selectedCollection || !collectionName.trim()) return;
    
    const updated = await updateCollection(selectedCollection.id, {
      name: collectionName,
      description: collectionDescription
    });
    
    if (updated) {
      const updatedCollections = collections.map(c => 
        c.id === selectedCollection.id ? { ...c, name: collectionName, description: collectionDescription } : c
      );
      setCollections(updatedCollections);
      setSelectedCollection({ ...selectedCollection, name: collectionName, description: collectionDescription });
      setShowEditCollectionDialog(false);
    }
  };

  const handleDeleteCollection = async () => {
    if (!selectedCollection) return;
    
    const success = await deleteCollection(selectedCollection.id);
    if (success) {
      const remainingCollections = collections.filter(c => c.id !== selectedCollection.id);
      setCollections(remainingCollections);
      setSelectedCollection(null);
      setCollectionPrompts([]);
    }
  };

  const openEditCollectionDialog = () => {
    if (!selectedCollection) return;
    
    setCollectionName(selectedCollection.name);
    setCollectionDescription(selectedCollection.description || "");
    setShowEditCollectionDialog(true);
  };

  const handleSelectCollection = async (collection: PromptCollection) => {
    setSelectedCollection(collection);
    setIsLoading(true);
    
    const prompts = await getCollectionPrompts(collection.id);
    setCollectionPrompts(prompts);
    
    setIsLoading(false);
  };

  const backToCollections = () => {
    setSelectedCollection(null);
    setCollectionPrompts([]);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <div className="flex-1 pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <header className="mb-8">
            <h1 className="text-3xl font-bold">Prompt Library</h1>
            <p className="mt-2 text-muted-foreground">
              Manage your saved prompts and collections
            </p>
          </header>
          
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search your prompt library..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <Tabs 
            value={activeTab} 
            onValueChange={setActiveTab}
            className="w-full"
          >
            <div className="border-b border-border">
              <TabsList className="justify-start bg-transparent h-14 p-0 gap-8">
                <TabsTrigger 
                  value="saved"
                  className="flex items-center gap-2 h-full rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-0"
                >
                  <Bookmark className="h-4 w-4" />
                  <span>All Saved</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="collections"
                  className="flex items-center gap-2 h-full rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-0"
                >
                  <Folder className="h-4 w-4" />
                  <span>Collections</span>
                </TabsTrigger>
              </TabsList>
            </div>
            
            <div className="pt-6">
              <TabsContent value="saved" className="m-0">
                {isLoading ? (
                  <LoadingState />
                ) : savedPrompts.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filterPrompts(savedPrompts).map((savedPrompt) => (
                      <PromptCard
                        key={savedPrompt.id}
                        prompt={mapSavedPromptToCardProps(savedPrompt)}
                      />
                    ))}
                  </div>
                ) : (
                  <EmptyState 
                    message="You haven't saved any prompts yet." 
                    action={
                      <Button asChild>
                        <a href="/explore" className="flex items-center gap-2">
                          <BookmarkPlus className="h-4 w-4" />
                          Browse Prompts to Save
                        </a>
                      </Button>
                    }
                  />
                )}
              </TabsContent>
              
              <TabsContent value="collections" className="m-0">
                {isLoading ? (
                  <LoadingState />
                ) : selectedCollection ? (
                  <div>
                    <div className="flex justify-between items-center mb-6">
                      <div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={backToCollections}
                          className="mb-2"
                        >
                          ← Back to Collections
                        </Button>
                        <h2 className="text-2xl font-bold">{selectedCollection.name}</h2>
                        {selectedCollection.description && (
                          <p className="mt-1 text-muted-foreground">{selectedCollection.description}</p>
                        )}
                      </div>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={openEditCollectionDialog}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit Collection
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={handleDeleteCollection}
                            className="text-destructive"
                          >
                            <Trash className="h-4 w-4 mr-2" />
                            Delete Collection
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    <Separator className="mb-6" />
                    
                    {collectionPrompts.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filterPrompts(collectionPrompts).map((savedPrompt) => (
                          <PromptCard
                            key={savedPrompt.id}
                            prompt={mapSavedPromptToCardProps(savedPrompt)}
                          />
                        ))}
                      </div>
                    ) : (
                      <EmptyState 
                        message="This collection is empty." 
                        action={
                          <Button asChild>
                            <a href="/explore" className="flex items-center gap-2">
                              <Plus className="h-4 w-4" />
                              Add Prompts to Collection
                            </a>
                          </Button>
                        }
                      />
                    )}
                  </div>
                ) : collections.length > 0 ? (
                  <div>
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-semibold">Your Collections</h2>
                      <Button onClick={() => setShowNewCollectionDialog(true)}>
                        <FolderPlus className="h-4 w-4 mr-2" />
                        New Collection
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {collections.map((collection) => (
                        <div 
                          key={collection.id}
                          className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-border/40 p-6 hover:shadow-md transition-all duration-300 cursor-pointer"
                          onClick={() => handleSelectCollection(collection)}
                        >
                          <div className="flex justify-between items-start mb-4">
                            <div className="bg-primary/10 rounded-full p-3">
                              <Folder className="h-6 w-6 text-primary" />
                            </div>
                          </div>
                          
                          <h3 className="text-lg font-semibold mb-2">{collection.name}</h3>
                          
                          {collection.description && (
                            <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                              {collection.description}
                            </p>
                          )}
                          
                          <div className="text-sm text-muted-foreground">
                            {collection.prompts_count} {collection.prompts_count === 1 ? 'prompt' : 'prompts'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <EmptyState 
                    message="You don't have any collections yet." 
                    action={
                      <Button onClick={() => setShowNewCollectionDialog(true)}>
                        <FolderPlus className="h-4 w-4 mr-2" />
                        Create Your First Collection
                      </Button>
                    }
                  />
                )}
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>
      
      <Footer />
      
      <Dialog open={showNewCollectionDialog} onOpenChange={setShowNewCollectionDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Collection</DialogTitle>
            <DialogDescription>
              Create a new collection to organize your saved prompts.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="collection-name">Collection Name</Label>
              <Input
                id="collection-name"
                value={collectionName}
                onChange={(e) => setCollectionName(e.target.value)}
                placeholder="My Awesome Collection"
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="collection-description">Description (Optional)</Label>
              <Textarea
                id="collection-description"
                value={collectionDescription}
                onChange={(e) => setCollectionDescription(e.target.value)}
                placeholder="What kind of prompts will you store here?"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCollectionDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateCollection} 
              disabled={!collectionName.trim()}
            >
              Create Collection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={showEditCollectionDialog} onOpenChange={setShowEditCollectionDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Collection</DialogTitle>
            <DialogDescription>
              Update your collection details.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-collection-name">Collection Name</Label>
              <Input
                id="edit-collection-name"
                value={collectionName}
                onChange={(e) => setCollectionName(e.target.value)}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="edit-collection-description">Description (Optional)</Label>
              <Textarea
                id="edit-collection-description"
                value={collectionDescription}
                onChange={(e) => setCollectionDescription(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditCollectionDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleEditCollection} 
              disabled={!collectionName.trim()}
            >
              Update Collection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PromptLibraryPage;
